// ============================================================================
// view.pigs — the pig feed ledger, off App's boot entirely
// ============================================================================
// The surface id is `view.pigs` and the route is /pig/feed. That mismatch is
// not a mistake: `pigs` is the live view string for PigFeedView, recorded as
// such in src/pig/pigRoutes.ts, and renaming it would move the URL adapter, the
// Header, globalNav and the access predicates all at once.
//
// EIGHT values reached this page through FOUR App channels, and not one of them
// was its own read:
//
//   ppp-feed-orders-v1          a PigEntry prop
//   ppp-pig-feed-inventory-v1   a PigEntry prop
//   ppp-breeding-v1             PigContext.breedingCycles
//   ppp-farrowing-v1            PigContext.farrowingRecs
//   ppp-feeders-v1              PigContext.feederGroups
//   ppp-breeders-v1             PigContext.breeders
//   ppp-feed-costs-v1           FeedCostsContext.feedCosts
//   pig_dailys (full history)   DailysRecentContext.pigDailys
//
// `pig.feed.core` owns all eight, which is what lets `pigs` retire from BOTH
// frozen consumer lists it appears in — app_store and pig_dailys. It was in
// every one of them it could be in: this page never touched layer_batches,
// layer_housings or poultry_dailys.
//
// WHY THIS IS PERMANENT AND NOT PARTIAL.
//
// The three props that remain are a WRITE (`sbSave`) and two SETTERS
// (`setFeedOrders`, `setPigFeedInventory`). Neither setter is a render read of
// a legacy boot value, and `residualLegacy` inventories reads: the provenance
// derivation scores a setter as non-consuming precisely so a localization is
// not forced to tear out a mirror it does not own.
//
// WHY THE SETTERS WERE NOT VESTIGIAL, IN THE PAST TENSE, and this is the exact
// mirror of what view.feed's declaration says about its own pair. App's
// `feedOrders` state was ALSO BroilerFeedView's — the ppp-feed-orders-v1 blob
// carries the starter, grower and layerfeed maps in the same object as the pig
// one — so a pig order that skipped `setFeedOrders` would leave /broiler/feed
// rendering a stale blob for the rest of the session. `setPigFeedInventory`
// keeps App's own copy of the physical count in step for the same reason, even
// though this page is currently its only reader.
//
// THE `feedOrders` HALF OF THAT IS NO LONGER TRUE OF THE TREE. Neither page
// renders from App's copy any more — both read the blob through their own route
// resource plus a local optimistic override — and neither domain entry even
// forwards it: `<PigFeedView setFeedOrders={…} setPigFeedInventory={…}
// sbSave={…} />` passes no `feedOrders`, and BroilerEntry's header records the
// same ("THE VALUE IS NO LONGER FORWARDED, AND THE TYPE STILL DECLARES IT").
// App's state is no longer seeded by a global boot fan-out. It remains only as
// write-compatibility state; no render read of it survives in src/.
//
// THIS PAIR IS NOW SYMMETRIC. view.feed said "retiring that mirror is
// view.pigs' work, not this surface's". This is that work: both halves now
// render through their own released resources, and the retained setters are not
// a boot dependency.
//
// WHAT DID END IS THE PAIR'S RELEASE BLOCKER.
//
// The symmetry above had a sharp edge. Both halves persisted the WHOLE
// ppp-feed-orders-v1 document — one object carrying the pig order map AND the
// three poultry maps — merged onto whatever base their own mount held. The
// shared `feedOrders` state was what kept that base current for both, so the
// mirror was load-bearing for CORRECTNESS and not merely for repaint. On a
// released mount there is no such state, and each half would have silently
// overwritten the other's orders: a poultry save would delete the pig order
// placed since /broiler/feed loaded, with no error anywhere.
//
// The write is now a PATCH naming only `pig`, merged by src/lib/feedOrdersApi
// onto a read of ppp-feed-orders-v1 taken at SAVE TIME. A pig save preserves
// the three poultry maps exactly, whether or not App's state exists, and it
// fails CLOSED — an unavailable read refuses the save rather than publishing a
// half document. `setFeedOrders` is therefore now called for a state nothing
// reads, and it is kept deliberately as write compatibility. Both released
// paths are correct whether that mirror is populated or empty.
//
// Both halves of the pair moved together, in one change, because a fix to one
// half alone would still have let the other clobber it. Both now have ledgered
// released routes; the `permanent` declaration remains their data-owner proof.
//
// THIS DECLARATION COVERS DATA OWNERSHIP, WHILE THE LEDGER/BORDER/RENDERER
// COMBINATION COVERS RELEASE. Those pieces are now all present: this surface
// has no `dataLoaded` gate or broad-loader dependency, and its retained setters
// do not change that fact. PigContext mirrors are likewise local compatibility
// state; each released pig surface owns the data it renders through its route
// resources.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.pigs',
  obligations: Object.freeze([Object.freeze({id: 'pig.feed.core', owner: 'permanent'})]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
